import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import Header from "../components/Header";
import { getCookies,removeCookie } from "../controllers/cookies.controller";
import globals from "../globals";

import fxRightImg from "../img/fx-right.svg";
import fxLeftImg from "../img/fx-left.svg";

import "../styles/subscription.css";

interface IUser {
    nome: string;
    email: string;
    cpf_cnpj: string;
    telefone: string;
}

interface IRecebimento {
    id: number;
    valor: string;
    vencimento: string;
    status: 'R' | 'A'; // R = Recebido, A = Aguardando
    descricao: string;
    tipo_recebimento: 'Transferencia' | 'Gateway' | 'Boleto' | 'Cartão' | 'PIX';
}

interface IESim {
    qrcode?: string;
    qrcode_base64: string;
    sm_dp_address: string;
    activation_code: string;
}

interface IPortabilidade {
    status: 'success' | 'error' | null;
    msg?: string;
    numero_origem?: string;
    numero_destino?: string;
    data_agendamento?: string;
    gateway_id?: number;
    portabilidade_ownership?: boolean;
    details?: string;
}

interface IMvnoData {
    status: false | 'PENDENTE' | 'CONFIGURANDO' | 'AGUARDANDO_ATIVACAO' | 'ATIVO' | 'SUSPENSO' | 'CANCELADO';
    chip_type?: 'ESIM' | 'FISICO';
    esim?: IESim;
    iccid?: string;
    linha_ativa: boolean;
    portabilidade?: IPortabilidade;
}

interface ICancelamentoAgendado {
    id: number;
    data_cancelamento: string;
    motivo: string;
    cancelar_internet: boolean;
    cancelar_tv: boolean;
    cancelar_mvno: boolean;
    status: 'PENDENTE' | 'PROCESSADO' | 'CANCELADO';
}

interface IAssinatura {
    id: string;
    contrato: string;
    data_ativacao: string;
    data_expiracao: string;
    data_pagamento: string;
    valor_plano: string | null;
    status: string;
    a_receber: IRecebimento[];
    tipo_plano: 'TELEFONIA_MOVEL' | 'STREAMING' | 'INTERNET' | 'outros';
    mvno?: IMvnoData;
    cancelamento_agendado?: ICancelamentoAgendado | null;
    status_assinatura?: 'PENDENTE' | 'CONFIGURANDO' | 'AGUARDANDO_ATIVACAO' | 'ATIVO' | 'SUSPENSO' | 'CANCELADO' | null;
}

const Subscription = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<IUser | null>(null);
    const [assinaturas, setAssinaturas] = useState<IAssinatura[]>([]);
    const [loading, setLoading] = useState(true);
    const [activatingMvno, setActivatingMvno] = useState<string | null>(null);
    const [showQRCode, setShowQRCode] = useState<{ [key: string]: boolean }>({});
    const [selectedAreaCode, setSelectedAreaCode] = useState<{ [key: string]: string }>({});
    const [selectedChipType, setSelectedChipType] = useState<{ [key: string]: 'ESIM' | 'FISICO' | '' }>({});
    const [iccidInput, setIccidInput] = useState<{ [key: string]: string }>({});
    const [cancellingContract, setCancellingContract] = useState<string | null>(null);
    const [showCancelModal, setShowCancelModal] = useState<string | null>(null);
    const [cancelReason, setCancelReason] = useState<string>('');
    const [cancelOptions, setCancelOptions] = useState<{ [key: string]: { internet: boolean, tv: boolean, mvno: boolean } }>({});
    
    // Estados para Portabilidade
    const [portabilidadeData, setPortabilidadeData] = useState<{ [key: string]: { ativar: boolean; numeroAtual: string; dataAgendamento: string; mudancaTitular: boolean; novoTitular: string; cpfNovoTitular: string } }>({});
    const [portabilidadeDatas, setPortabilidadeDatas] = useState<{ [key: string]: Array<{ start: string; end: string }> }>({});
    const [loadingPortabilidadeDatas, setLoadingPortabilidadeDatas] = useState<{ [key: string]: boolean }>({});

    // Função para formatar CPF/CNPJ
    const formatarDocumento = (documento: string): string => {
        const cleaned = documento.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        if (cleaned.length === 14) {
            return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        return documento;
    };

    // Função para formatar número de telefone
    const formatarTelefone = (numero: string): string => {
        const cleaned = numero.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        }
        return numero;
    };

    useEffect(() => {
        const token = getCookies('token');
        // Token já validado pelo ProtectedRoute
        if (token) {
            loadUserData(token);
        } else {
            navigate('/');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Inicializar DDD automaticamente quando usuário e assinaturas forem carregados
    useEffect(() => {
        if (user?.telefone && assinaturas.length > 0) {
            const defaultDDD = user.telefone.substring(1, 3); // Pega os 2 dígitos após o primeiro caractere
            const newSelectedAreaCodes: { [key: string]: string } = {};

            assinaturas.forEach(assinatura => {
                // Preencher DDD para assinaturas MVNO que ainda não foram configuradas (status === false)
                if (assinatura.tipo_plano === 'TELEFONIA_MOVEL' && assinatura.mvno?.status === false) {
                    newSelectedAreaCodes[assinatura.id] = defaultDDD;
                }
            });

            if (Object.keys(newSelectedAreaCodes).length > 0) {
                setSelectedAreaCode(newSelectedAreaCodes);
            }
        }
    }, [user, assinaturas]);

    const loadUserData = async (token: string) => {
        try {
            // Buscar dados do usuário
            const userResponse = await fetch(`${globals.apiBaseUrl}/api/contratoonline/user`, {
                method: 'GET',
                headers: {
                    'Authorization': token
                }
            });

            const userData = await userResponse.json();

            if (userData.status === 'sucess') {
                setUser(userData.user);
            } else {
                throw new Error('Falha ao carregar dados do usuário');
            }

            // Buscar assinaturas
            const assinaturasResponse = await fetch(`${globals.apiBaseUrl}/api/contratoonline/assinaturas`, {
                method: 'GET',
                headers: {
                    'Authorization': token
                }
            });

            const assinaturasData = await assinaturasResponse.json();

            if (assinaturasData.status === 'success') {
                setAssinaturas(assinaturasData.data);
            } else {
                console.error('Falha ao carregar assinaturas:', assinaturasData);
            }
        } catch (error) {
            console.error("Erro ao carregar dados:", error);
            removeCookie('token');
            navigate('/login');
        } finally {
            setLoading(false);
        }
    };

    const ativarMVNO = async (contratoId: string, chipType: 'ESIM' | 'FISICO', iccid?: string) => {
        console.log(`Ativando MVNO para contrato ${contratoId} com chip type ${chipType}`);
        const token = getCookies('token');
        if (!token) return;

        const areaCode = selectedAreaCode[contratoId];
        if (!areaCode) {
            alert('Por favor, selecione o código de área (DDD)');
            return;
        }

        if (chipType === 'FISICO' && !iccid) {
            alert('Por favor, informe o ICCID do chip físico');
            return;
        }

        setActivatingMvno(contratoId);

        try {
            const body: any = {
                contrato_id: contratoId,
                chip_type: chipType,
                area_code: areaCode
            };

            // Adicionar ICCID se for chip físico
            if (chipType === 'FISICO' && iccid) {
                body.iccid = iccid;
            }

            // Adicionar dados de portabilidade se ativada
            const portData = portabilidadeData[contratoId];
            if (portData?.ativar) {
                body.portabilidade = true;
                body.numero_origem = portData.numeroAtual.replace(/\D/g, ''); // Remove formatação
                body.data_agendamento_portabilidade = portData.dataAgendamento;
                body.portabilidade_ownership = portData.mudancaTitular;
                
                if (portData.mudancaTitular) {
                    body.owner_name = portData.novoTitular;
                    body.owner_document = portData.cpfNovoTitular.replace(/\D/g, ''); // Remove formatação
                }
            }

            const response = await fetch(`${globals.apiBaseUrl}/api/contratoonline/mvno/config`, {
                method: 'POST',
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (data.status === 'success' && data.assinatura) {
                // Atualizar assinatura com os dados retornados pela API
                setAssinaturas(prev => prev.map(assinatura =>
                    assinatura.id === contratoId
                        ? {
                            ...assinatura,
                            mvno: {
                                status: data.assinatura.status as IMvnoData['status'],
                                chip_type: data.assinatura.chip_type || chipType,
                                esim: data.assinatura.esim,
                                linha_ativa: true,
                                portabilidade: data.portabilidade || undefined
                            }
                        }
                        : assinatura
                ));

                // Mostrar QR code se for e-SIM e tiver dados do e-SIM
                if (chipType === 'ESIM' && data.assinatura.esim) {
                    setShowQRCode(prev => ({ ...prev, [contratoId]: true }));
                }

                // Limpar dados de portabilidade após sucesso
                setPortabilidadeData(prev => {
                    const newData = { ...prev };
                    delete newData[contratoId];
                    return newData;
                });

                alert(data.msg || 'Linha MVNO ativada com sucesso!');
            } else {
                alert(data.msg || data.message || 'Erro ao ativar linha MVNO');
            }
        } catch (error) {
            console.error('Erro ao ativar MVNO:', error);
            alert('Erro ao ativar linha MVNO');
        } finally {
            setActivatingMvno(null);
        }
    };

    const toggleQRCode = (contratoId: string) => {
        setShowQRCode(prev => ({ ...prev, [contratoId]: !prev[contratoId] }));
    };

    const carregarDatasPortabilidade = async (contratoId: string) => {
        const token = getCookies('token');
        if (!token) return;

        setLoadingPortabilidadeDatas(prev => ({ ...prev, [contratoId]: true }));

        try {
            const response = await fetch(`${globals.apiBaseUrl}/api/contratoonline/mvno/portabilidade/datas`, {
                method: 'GET',
                headers: {
                    'Authorization': token
                }
            });

            const data = await response.json();

            if (data.success && data.data && Array.isArray(data.data)) {
                setPortabilidadeDatas(prev => ({ ...prev, [contratoId]: data.data }));
            } else {
                console.warn('Nenhuma data de portabilidade disponível:', data.message);
                setPortabilidadeDatas(prev => ({ ...prev, [contratoId]: [] }));
                alert(data.message || 'Nenhuma janela de portabilidade disponível no momento');
            }
        } catch (error) {
            console.error('Erro ao carregar datas de portabilidade:', error);
            alert('Erro ao carregar datas de portabilidade disponíveis');
        } finally {
            setLoadingPortabilidadeDatas(prev => ({ ...prev, [contratoId]: false }));
        }
    };

    const handleAtivarPortabilidade = (contratoId: string) => {
        const portData = portabilidadeData[contratoId];
        
        // Se já tinha dados, carrega as datas novamente
        if (portData?.ativar) {
            carregarDatasPortabilidade(contratoId);
        }
    };

    const handleCancelContract = async (contratoId: string) => {
        const token = getCookies('token');
        if (!token) return;

        const options = cancelOptions[contratoId] || { internet: true, tv: true, mvno: true };

        if (!window.confirm('Tem certeza que deseja solicitar o cancelamento desta assinatura? O cancelamento será processado no próximo vencimento.')) {
            return;
        }

        setCancellingContract(contratoId);

        try {
            const response = await fetch(`${globals.apiBaseUrl}/api/contratoonline/cancelamento`, {
                method: 'POST',
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id_contrato: parseInt(contratoId),
                    cancelar_internet: options.internet,
                    cancelar_tv: options.tv,
                    cancelar_mvno: options.mvno,
                    motivo: cancelReason || 'Cliente solicitou cancelamento'
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                alert(`Cancelamento agendado com sucesso!\nData de cancelamento: ${data.data_cancelamento}`);

                // Atualizar a assinatura com os dados de cancelamento
                setAssinaturas(prev => prev.map(assinatura =>
                    assinatura.id === contratoId
                        ? {
                            ...assinatura,
                            cancelamento_agendado: {
                                id: data.cancelamento_id,
                                data_cancelamento: data.data_cancelamento,
                                motivo: data.motivo,
                                cancelar_internet: data.servicos_agendados.internet,
                                cancelar_tv: data.servicos_agendados.tv,
                                cancelar_mvno: data.servicos_agendados.mvno,
                                status: 'PENDENTE'
                            }
                        }
                        : assinatura
                ));

                setShowCancelModal(null);
                setCancelReason('');
            } else {
                alert(data.msg || 'Erro ao agendar cancelamento');
            }
        } catch (error) {
            console.error('Erro ao cancelar contrato:', error);
            alert('Erro ao agendar cancelamento');
        } finally {
            setCancellingContract(null);
        }
    };

    const openCancelModal = (contratoId: string) => {
        setShowCancelModal(contratoId);
        setCancelOptions(prev => ({
            ...prev,
            [contratoId]: { internet: true, tv: true, mvno: true }
        }));
    };

    const renderStatus = (status: string) => {
        const statusMap: { [key: string]: { text: string; color: string } } = {
            'PENDENTE': { text: 'Pendente', color: '#FFC107' },
            'CONFIGURANDO': { text: 'Configurando', color: '#2196F3' },
            'AGUARDANDO_ATIVACAO': { text: 'Aguardando Ativação', color: '#FF9800' },
            'ATIVO': { text: 'Ativo', color: '#4CAF50' },
            'SUSPENSO': { text: 'Suspenso', color: '#F44336' },
            'CANCELADO': { text: 'Cancelado', color: '#9E9E9E' },
            // Manter compatibilidade com códigos antigos
            'A': { text: 'Ativo', color: '#4CAF50' },
            'P': { text: 'Pendente', color: '#FFC107' },
            'S': { text: 'Suspenso', color: '#F44336' },
            'C': { text: 'Cancelado', color: '#9E9E9E' }
        };

        const statusInfo = statusMap[status] || { text: status, color: '#999' };

        return (
            <span style={{ color: statusInfo.color, fontWeight: 'bold' }}>
                {statusInfo.text}
            </span>
        );
    };

    const renderPaymentStatus = (status: 'R' | 'A') => {
        return status === 'R' ? (
            <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>Pago</span>
        ) : (
            <span style={{ color: '#FFC107', fontWeight: 'bold' }}>Aguardando</span>
        );
    };

    const formatDate = (dateString: string) => {
        if (!dateString || dateString === '0000-00-00') return 'N/A';
        return dateString;
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="subscription-container">
                    <b className="header">Carregando...</b>
                </div>
            </React.Fragment>
        );
    }

    if (!user) {
        return (
            <React.Fragment>
                <Header />
                <div className="subscription-container">
                    <b className="header">Não foi possível carregar seus dados.</b>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="subscription-container">
                <b className="header">Bem-vindo, {user.nome}</b>

                <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#1a1a1a', borderRadius: '12px' }}>
                    <h3 style={{ color: '#fff', marginBottom: '15px' }}>Meus Dados</h3>
                    <p style={{ color: '#ccc', margin: '5px 0' }}><strong>Email:</strong> {user.email}</p>
                    <p style={{ color: '#ccc', margin: '5px 0' }}><strong>CPF/CNPJ:</strong> {user.cpf_cnpj}</p>
                    <p style={{ color: '#ccc', margin: '5px 0' }}><strong>Telefone:</strong> {user.telefone}</p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <b className="header">Minhas Assinaturas</b>
                    <button
                        onClick={() => navigate('/selecione-plano')}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: '#6EE7B7',
                            color: '#000',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#5cd6a5';
                            e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#6EE7B7';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                    >
                        + Nova Assinatura
                    </button>
                </div>

                {assinaturas.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                        Você ainda não possui assinaturas ativas.
                    </div>
                ) : (
                    assinaturas.map((assinatura) => (
                        <div
                            key={assinatura.id}
                            style={{
                                backgroundColor: '#1a1a1a',
                                padding: '20px',
                                borderRadius: '12px',
                                marginBottom: '20px'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                                <div>
                                    <h3 style={{ color: '#fff', margin: '0 0 10px 0' }}>{assinatura.contrato}</h3>
                                    <p style={{ color: '#999', margin: '5px 0' }}>
                                        Status: {renderStatus(assinatura.status)}
                                    </p>
                                    <p style={{ color: '#666', fontSize: '12px', margin: '5px 0', textTransform: 'capitalize' }}>
                                        Tipo: {assinatura.tipo_plano.replace('_', ' ').toLowerCase()}
                                    </p>
                                </div>
                                {!assinatura.cancelamento_agendado && assinatura.status !== 'C' && assinatura.status !== 'CANCELADO' && (
                                    <button
                                        onClick={() => openCancelModal(assinatura.id)}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#F44336',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#D32F2F';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#F44336';
                                        }}
                                    >
                                        Solicitar Cancelamento
                                    </button>
                                )}
                            </div>

                            {assinatura.cancelamento_agendado && (
                                <div style={{
                                    padding: '15px',
                                    backgroundColor: '#2a2a2a',
                                    borderRadius: '8px',
                                    borderLeft: '4px solid #FF9800',
                                    marginBottom: '15px'
                                }}>
                                    <h4 style={{ color: '#FF9800', margin: '0 0 10px 0', fontSize: '16px' }}>
                                        ⚠️ Cancelamento Agendado
                                    </h4>
                                    <p style={{ color: '#ccc', margin: '5px 0', fontSize: '14px' }}>
                                        <strong>Data do cancelamento:</strong> {assinatura.cancelamento_agendado.data_cancelamento}
                                    </p>
                                    <p style={{ color: '#ccc', margin: '5px 0', fontSize: '14px' }}>
                                        <strong>Motivo:</strong> {assinatura.cancelamento_agendado.motivo}
                                    </p>
                                    <p style={{ color: '#ccc', margin: '5px 0', fontSize: '14px' }}>
                                        <strong>Status:</strong> {assinatura.cancelamento_agendado.status}
                                    </p>
                                    <div style={{ marginTop: '10px' }}>
                                        <p style={{ color: '#999', fontSize: '12px', margin: '5px 0' }}>Serviços a serem cancelados:</p>
                                        <ul style={{ color: '#ccc', fontSize: '13px', marginLeft: '20px' }}>
                                            {assinatura.cancelamento_agendado.cancelar_internet && <li>Internet</li>}
                                            {assinatura.cancelamento_agendado.cancelar_tv && <li>TV</li>}
                                            {assinatura.cancelamento_agendado.cancelar_mvno && <li>Telefonia Móvel (MVNO)</li>}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                                <div>
                                    <p style={{ color: '#999', fontSize: '12px', margin: '0 0 5px 0' }}>Data de Ativação</p>
                                    <p style={{ color: '#fff', margin: 0 }}>{formatDate(assinatura.data_ativacao)}</p>
                                </div>
                                <div>
                                    <p style={{ color: '#999', fontSize: '12px', margin: '0 0 5px 0' }}>Data de Expiração</p>
                                    <p style={{ color: '#fff', margin: 0 }}>{formatDate(assinatura.data_expiracao)}</p>
                                </div>
                                <div>
                                    <p style={{ color: '#999', fontSize: '12px', margin: '0 0 5px 0' }}>Data de Pagamento</p>
                                    <p style={{ color: '#fff', margin: 0 }}>{formatDate(assinatura.data_pagamento)}</p>
                                    <p style={{ color: '#fff', margin: 0 }}>#{assinatura.tipo_plano}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Valor do Plano</p>
                                    <p className="text-white font-semibold">{assinatura.valor_plano ? `R$ ${assinatura.valor_plano}` : 'N/A'}</p>
                                </div>
                            </div>

                            {assinatura.tipo_plano === 'TELEFONIA_MOVEL'  && assinatura.mvno?.status !== false &&(
                                <div style={{ marginTop: '20px', borderTop: '1px solid #333', paddingTop: '20px' }}>
                                    <h4 style={{ color: '#fff', margin: '0 0 15px 0', fontSize: '16px' }}>Telefonia Móvel (MVNO)</h4>

                                    {assinatura.mvno?.status === 'PENDENTE' ? (
                                        <div style={{ padding: '15px', backgroundColor: '#2a2a2a', borderRadius: '8px', borderLeft: '4px solid #FFC107' }}>
                                            <p style={{ color: '#FFC107', margin: 0, fontSize: '14px' }}>
                                                ⚠️ Sua assinatura está pendente. A configuração da linha móvel será liberada após a confirmação do pagamento.
                                            </p>
                                        </div>
                                    ) : assinatura.mvno?.status === 'AGUARDANDO_ATIVACAO' ? (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#4CAF50"/>
                                                </svg>
                                                <p style={{ color: '#4CAF50', margin: 0, fontWeight: 'bold' }}>
                                                    Linha configurada - {assinatura.mvno?.chip_type}
                                                </p>
                                            </div>

                                            {/* Exibir status de Portabilidade se ativada (funciona para FISICO e ESIM) */}
                                            {assinatura.mvno.portabilidade && (
                                                <div style={{
                                                    backgroundColor: assinatura.mvno.portabilidade.status === 'success' ? '#1a3a1a' : '#3a1a1a',
                                                    border: `2px solid ${assinatura.mvno.portabilidade.status === 'success' ? '#4CAF50' : '#F44336'}`,
                                                    padding: '15px',
                                                    borderRadius: '8px',
                                                    marginBottom: '15px'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                                        {assinatura.mvno.portabilidade.status === 'success' ? (
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#4CAF50"/>
                                                            </svg>
                                                        ) : (
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="#F44336"/>
                                                            </svg>
                                                        )}
                                                        <h4 style={{ color: assinatura.mvno.portabilidade.status === 'success' ? '#4CAF50' : '#F44336', margin: 0 }}>
                                                            Portabilidade: {assinatura.mvno.portabilidade.msg}
                                                        </h4>
                                                    </div>
                                                    {assinatura.mvno.portabilidade.numero_origem && (
                                                        <p style={{ color: '#ccc', margin: '5px 0', fontSize: '14px' }}>
                                                            <strong>Número de origem:</strong> {assinatura.mvno.portabilidade.numero_origem}
                                                        </p>
                                                    )}
                                                    {assinatura.mvno.portabilidade.numero_destino && (
                                                        <p style={{ color: '#ccc', margin: '5px 0', fontSize: '14px' }}>
                                                            <strong>Número de destino:</strong> {assinatura.mvno.portabilidade.numero_destino}
                                                        </p>
                                                    )}
                                                    {assinatura.mvno.portabilidade.data_agendamento && (
                                                        <p style={{ color: '#ccc', margin: '5px 0', fontSize: '14px' }}>
                                                            <strong>Agendada para:</strong> {new Date(assinatura.mvno.portabilidade.data_agendamento).toLocaleString('pt-BR')}
                                                        </p>
                                                    )}
                                                    {assinatura.mvno.portabilidade.gateway_id && (
                                                        <p style={{ color: '#999', margin: '5px 0', fontSize: '12px' }}>
                                                            <strong>ID Telecall:</strong> {assinatura.mvno.portabilidade.gateway_id}
                                                        </p>
                                                    )}
                                                    {assinatura.mvno.portabilidade.portabilidade_ownership && (
                                                        <p style={{ color: '#999', margin: '5px 0', fontSize: '12px' }}>
                                                            <strong>Mudança de titularidade:</strong> Sim
                                                        </p>
                                                    )}
                                                    {assinatura.mvno.portabilidade.details && (
                                                        <p style={{ color: '#F44336', margin: '5px 0', fontSize: '12px' }}>
                                                            <strong>Detalhes:</strong> {assinatura.mvno.portabilidade.details}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {assinatura.mvno?.chip_type === 'ESIM' && assinatura.mvno.esim && (
                                                <div>
                                                    <button
                                                        onClick={() => toggleQRCode(assinatura.id)}
                                                        style={{
                                                            backgroundColor: '#6EE7B7',
                                                            color: '#000',
                                                            border: 'none',
                                                            padding: '10px 20px',
                                                            borderRadius: '6px',
                                                            fontWeight: 'bold',
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            marginBottom: '15px'
                                                        }}
                                                    >
                                                        {showQRCode[assinatura.id] ? 'Ocultar QR Code' : 'Exibir QR Code do e-SIM'}
                                                    </button>

                                                    {showQRCode[assinatura.id] && (
                                                        <div style={{
                                                            backgroundColor: '#fff',
                                                            padding: '20px',
                                                            borderRadius: '8px',
                                                            textAlign: 'center'
                                                        }}>
                                                            <h5 style={{ color: '#000', margin: '0 0 15px 0' }}>Escaneie o QR Code com seu dispositivo</h5>
                                                            <img
                                                                src={assinatura.mvno.esim.qrcode_base64}
                                                                alt="QR Code e-SIM"
                                                                style={{ maxWidth: '300px', width: '100%', height: 'auto' }}
                                                            />
                                                            <div style={{ marginTop: '15px', textAlign: 'left', color: '#666' }}>
                                                                <p style={{ margin: '5px 0', fontSize: '12px' }}>
                                                                    <strong>SM-DP+ Address:</strong><br />
                                                                    <code style={{ backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>
                                                                        {assinatura.mvno.esim.sm_dp_address}
                                                                    </code>
                                                                </p>
                                                                <p style={{ margin: '5px 0', fontSize: '12px' }}>
                                                                    <strong>Activation Code:</strong><br />
                                                                    <code style={{ backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', wordBreak: 'break-all' }}>
                                                                        {assinatura.mvno.esim.activation_code}
                                                                    </code>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : assinatura.mvno?.status === 'CONFIGURANDO' ? (
                                        <div>
                                            <p style={{ color: '#FFC107', marginBottom: '15px' }}>
                                                Sua linha ainda não foi configurada. Configure abaixo:
                                            </p>

                                            <div style={{ marginBottom: '20px' }}>
                                                <label style={{ color: '#fff', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                                                    Código de Área (DDD):
                                                </label>
                                                <select
                                                    value={selectedAreaCode[assinatura.id] || ''}
                                                    onChange={(e) => setSelectedAreaCode(prev => ({ ...prev, [assinatura.id]: e.target.value }))}
                                                    style={{
                                                        backgroundColor: '#2a2a2a',
                                                        color: '#fff',
                                                        border: '1px solid #444',
                                                        padding: '10px 15px',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        width: '100%',
                                                        maxWidth: '200px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <option value="">Selecione o DDD</option>
                                                    <option value="11">11 - São Paulo</option>
                                                    <option value="12">12 - São José dos Campos</option>
                                                    <option value="13">13 - Santos</option>
                                                    <option value="14">14 - Bauru</option>
                                                    <option value="15">15 - Sorocaba</option>
                                                    <option value="16">16 - Ribeirão Preto</option>
                                                    <option value="17">17 - São José do Rio Preto</option>
                                                    <option value="18">18 - Presidente Prudente</option>
                                                    <option value="19">19 - Campinas</option>
                                                    <option value="21">21 - Rio de Janeiro</option>
                                                    <option value="22">22 - Campos dos Goytacazes</option>
                                                    <option value="24">24 - Volta Redonda</option>
                                                    <option value="27">27 - Vitória</option>
                                                    <option value="28">28 - Cachoeiro de Itapemirim</option>
                                                    <option value="31">31 - Belo Horizonte</option>
                                                    <option value="32">32 - Juiz de Fora</option>
                                                    <option value="33">33 - Governador Valadares</option>
                                                    <option value="34">34 - Uberlândia</option>
                                                    <option value="35">35 - Poços de Caldas</option>
                                                    <option value="37">37 - Divinópolis</option>
                                                    <option value="38">38 - Montes Claros</option>
                                                    <option value="41">41 - Curitiba</option>
                                                    <option value="42">42 - Ponta Grossa</option>
                                                    <option value="43">43 - Londrina</option>
                                                    <option value="44">44 - Maringá</option>
                                                    <option value="45">45 - Foz do Iguaçu</option>
                                                    <option value="46">46 - Francisco Beltrão</option>
                                                    <option value="47">47 - Joinville</option>
                                                    <option value="48">48 - Florianópolis</option>
                                                    <option value="49">49 - Chapecó</option>
                                                    <option value="51">51 - Porto Alegre</option>
                                                    <option value="53">53 - Pelotas</option>
                                                    <option value="54">54 - Caxias do Sul</option>
                                                    <option value="55">55 - Santa Maria</option>
                                                    <option value="61">61 - Brasília</option>
                                                    <option value="62">62 - Goiânia</option>
                                                    <option value="63">63 - Palmas</option>
                                                    <option value="64">64 - Rio Verde</option>
                                                    <option value="65">65 - Cuiabá</option>
                                                    <option value="66">66 - Rondonópolis</option>
                                                    <option value="67">67 - Campo Grande</option>
                                                    <option value="68">68 - Rio Branco</option>
                                                    <option value="69">69 - Porto Velho</option>
                                                    <option value="71">71 - Salvador</option>
                                                    <option value="73">73 - Ilhéus</option>
                                                    <option value="74">74 - Juazeiro</option>
                                                    <option value="75">75 - Feira de Santana</option>
                                                    <option value="77">77 - Barreiras</option>
                                                    <option value="79">79 - Aracaju</option>
                                                    <option value="81">81 - Recife</option>
                                                    <option value="82">82 - Maceió</option>
                                                    <option value="83">83 - João Pessoa</option>
                                                    <option value="84">84 - Natal</option>
                                                    <option value="85">85 - Fortaleza</option>
                                                    <option value="86">86 - Teresina</option>
                                                    <option value="87">87 - Petrolina</option>
                                                    <option value="88">88 - Juazeiro do Norte</option>
                                                    <option value="89">89 - Picos</option>
                                                    <option value="91">91 - Belém</option>
                                                    <option value="92">92 - Manaus</option>
                                                    <option value="93">93 - Santarém</option>
                                                    <option value="94">94 - Marabá</option>
                                                    <option value="95">95 - Boa Vista</option>
                                                    <option value="96">96 - Macapá</option>
                                                    <option value="97">97 - Coari</option>
                                                    <option value="98">98 - São Luís</option>
                                                    <option value="99">99 - Imperatriz</option>
                                                </select>
                                            </div>
                                            {/* Opção de Portabilidade */}
                                            <div style={{ borderTop: '1px solid #333', paddingTop: '20px', marginTop: '20px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', color: '#fff', marginBottom: '15px', cursor: 'pointer' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={portabilidadeData[assinatura.id]?.ativar ?? false}
                                                        onChange={(e) => {
                                                            setPortabilidadeData(prev => ({
                                                                ...prev,
                                                                [assinatura.id]: {
                                                                    ativar: e.target.checked,
                                                                    numeroAtual: prev[assinatura.id]?.numeroAtual || '',
                                                                    dataAgendamento: prev[assinatura.id]?.dataAgendamento || '',
                                                                    mudancaTitular: prev[assinatura.id]?.mudancaTitular || false,
                                                                    novoTitular: prev[assinatura.id]?.novoTitular || '',
                                                                    cpfNovoTitular: prev[assinatura.id]?.cpfNovoTitular || ''
                                                                }
                                                            }));
                                                            // Carrega as datas quando portabilidade é ativada
                                                            if (e.target.checked) {
                                                                setTimeout(() => carregarDatasPortabilidade(assinatura.id), 100);
                                                            }
                                                        }}
                                                        style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                                                    />
                                                    <span>Desejo ativar portabilidade do meu número</span>
                                                </label>

                                                {portabilidadeData[assinatura.id]?.ativar && (
                                                    <div style={{ backgroundColor: '#2a2a2a', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #6EE7B7', marginTop: '15px' }}>
                                                        <div style={{ marginBottom: '15px' }}>
                                                            <label style={{ color: '#fff', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                                                                Número Atual (com DDD)*
                                                            </label>
                                                            <input
                                                                type="tel"
                                                                placeholder="(11) 98765-4321"
                                                                value={formatarTelefone(portabilidadeData[assinatura.id]?.numeroAtual || '')}
                                                                onChange={(e) => {
                                                                    const valor = e.target.value.replace(/\D/g, '');
                                                                    setPortabilidadeData(prev => ({
                                                                        ...prev,
                                                                        [assinatura.id]: {
                                                                            ...prev[assinatura.id],
                                                                            numeroAtual: valor
                                                                        }
                                                                    }));
                                                                }}
                                                                style={{
                                                                    width: '100%',
                                                                    backgroundColor: '#1a1a1a',
                                                                    color: '#fff',
                                                                    border: '1px solid #444',
                                                                    padding: '10px 15px',
                                                                    borderRadius: '6px',
                                                                    fontSize: '14px',
                                                                    boxSizing: 'border-box'
                                                                }}
                                                            />
                                                            <p style={{ color: '#999', fontSize: '12px', margin: '5px 0 0 0' }}>
                                                                Formato: 11987654321
                                                            </p>
                                                        </div>

                                                        <div style={{ marginBottom: '15px' }}>
                                                            <label style={{ color: '#fff', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                                                                Data/Hora de Agendamento*
                                                            </label>
                                                            {!portabilidadeDatas[assinatura.id] ? (
                                                                <button
                                                                    onClick={() => carregarDatasPortabilidade(assinatura.id)}
                                                                    disabled={loadingPortabilidadeDatas[assinatura.id]}
                                                                    style={{
                                                                        width: '100%',
                                                                        backgroundColor: '#6EE7B7',
                                                                        color: '#000',
                                                                        border: 'none',
                                                                        padding: '10px 15px',
                                                                        borderRadius: '6px',
                                                                        fontSize: '14px',
                                                                        fontWeight: 'bold',
                                                                        cursor: loadingPortabilidadeDatas[assinatura.id] ? 'not-allowed' : 'pointer',
                                                                        opacity: loadingPortabilidadeDatas[assinatura.id] ? 0.6 : 1
                                                                    }}
                                                                >
                                                                    {loadingPortabilidadeDatas[assinatura.id] ? 'Carregando datas...' : 'Carregar Datas Disponíveis'}
                                                                </button>
                                                            ) : portabilidadeDatas[assinatura.id]?.length === 0 ? (
                                                                <div style={{
                                                                    padding: '10px 15px',
                                                                    backgroundColor: '#3a1a1a',
                                                                    color: '#F44336',
                                                                    borderRadius: '6px',
                                                                    fontSize: '14px',
                                                                    textAlign: 'center'
                                                                }}>
                                                                    ❌ Nenhuma data disponível no momento. Tente novamente mais tarde.
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <select
                                                                        value={portabilidadeData[assinatura.id]?.dataAgendamento || ''}
                                                                        onChange={(e) => {
                                                                            setPortabilidadeData(prev => ({
                                                                                ...prev,
                                                                                [assinatura.id]: {
                                                                                    ...prev[assinatura.id],
                                                                                    dataAgendamento: e.target.value
                                                                                }
                                                                            }));
                                                                        }}
                                                                        style={{
                                                                            width: '100%',
                                                                            backgroundColor: '#1a1a1a',
                                                                            color: '#fff',
                                                                            border: '1px solid #444',
                                                                            padding: '10px 15px',
                                                                            borderRadius: '6px',
                                                                            fontSize: '14px',
                                                                            boxSizing: 'border-box',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >
                                                                        <option value="">Selecione uma data/hora</option>
                                                                        {portabilidadeDatas[assinatura.id]?.map((janela, index) => {
                                                                            const start = new Date(janela.start);
                                                                            const end = new Date(janela.end);
                                                                            const displayDate = start.toLocaleString('pt-BR', {
                                                                                weekday: 'short',
                                                                                year: 'numeric',
                                                                                month: '2-digit',
                                                                                day: '2-digit',
                                                                                hour: '2-digit',
                                                                                minute: '2-digit'
                                                                            });
                                                                            const displayEnd = end.toLocaleTimeString('pt-BR', {
                                                                                hour: '2-digit',
                                                                                minute: '2-digit'
                                                                            });
                                                                            return (
                                                                                <option key={index} value={janela.start}>
                                                                                    {displayDate} às {displayEnd}
                                                                                </option>
                                                                            );
                                                                        })}
                                                                    </select>
                                                                    <p style={{ color: '#999', fontSize: '12px', margin: '5px 0 0 0' }}>
                                                                        Selecione uma das janelas de horário disponíveis
                                                                    </p>
                                                                    <button
                                                                        onClick={() => carregarDatasPortabilidade(assinatura.id)}
                                                                        disabled={loadingPortabilidadeDatas[assinatura.id]}
                                                                        style={{
                                                                            marginTop: '8px',
                                                                            padding: '6px 12px',
                                                                            backgroundColor: '#444',
                                                                            color: '#fff',
                                                                            border: 'none',
                                                                            borderRadius: '4px',
                                                                            fontSize: '12px',
                                                                            cursor: loadingPortabilidadeDatas[assinatura.id] ? 'not-allowed' : 'pointer',
                                                                            opacity: loadingPortabilidadeDatas[assinatura.id] ? 0.6 : 1
                                                                        }}
                                                                    >
                                                                        🔄 Recarregar Datas
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>

                                                        <label style={{ display: 'flex', alignItems: 'center', color: '#fff', marginBottom: '15px', cursor: 'pointer' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={portabilidadeData[assinatura.id]?.mudancaTitular ?? false}
                                                                onChange={(e) => {
                                                                    setPortabilidadeData(prev => ({
                                                                        ...prev,
                                                                        [assinatura.id]: {
                                                                            ...prev[assinatura.id],
                                                                            mudancaTitular: e.target.checked
                                                                        }
                                                                    }));
                                                                }}
                                                                style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                                                            />
                                                            <span>Haverá mudança de titularidade</span>
                                                        </label>

                                                        {portabilidadeData[assinatura.id]?.mudancaTitular && (
                                                            <>
                                                                <div style={{ marginBottom: '15px' }}>
                                                                    <label style={{ color: '#fff', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                                                                        Nome do Novo Titular*
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Nome completo"
                                                                        value={portabilidadeData[assinatura.id]?.novoTitular || ''}
                                                                        onChange={(e) => {
                                                                            setPortabilidadeData(prev => ({
                                                                                ...prev,
                                                                                [assinatura.id]: {
                                                                                    ...prev[assinatura.id],
                                                                                    novoTitular: e.target.value
                                                                                }
                                                                            }));
                                                                        }}
                                                                        style={{
                                                                            width: '100%',
                                                                            backgroundColor: '#1a1a1a',
                                                                            color: '#fff',
                                                                            border: '1px solid #444',
                                                                            padding: '10px 15px',
                                                                            borderRadius: '6px',
                                                                            fontSize: '14px',
                                                                            boxSizing: 'border-box'
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div style={{ marginBottom: '15px' }}>
                                                                    <label style={{ color: '#fff', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                                                                        CPF/CNPJ do Novo Titular*
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="000.000.000-00 ou 00.000.000/0000-00"
                                                                        value={formatarDocumento(portabilidadeData[assinatura.id]?.cpfNovoTitular || '')}
                                                                        onChange={(e) => {
                                                                            const valor = e.target.value.replace(/\D/g, '');
                                                                            setPortabilidadeData(prev => ({
                                                                                ...prev,
                                                                                [assinatura.id]: {
                                                                                    ...prev[assinatura.id],
                                                                                    cpfNovoTitular: valor
                                                                                }
                                                                            }));
                                                                        }}
                                                                        style={{
                                                                            width: '100%',
                                                                            backgroundColor: '#1a1a1a',
                                                                            color: '#fff',
                                                                            border: '1px solid #444',
                                                                            padding: '10px 15px',
                                                                            borderRadius: '6px',
                                                                            fontSize: '14px',
                                                                            boxSizing: 'border-box'
                                                                        }}
                                                                    />
                                                                    <p style={{ color: '#999', fontSize: '12px', margin: '5px 0 0 0' }}>
                                                                        CPF (11 dígitos) ou CNPJ (14 dígitos)
                                                                    </p>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div style={{ marginBottom: '20px' }}>
                                                <label style={{ color: '#fff', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                                                    Escolha o tipo de chip:
                                                </label>
                                                <select
                                                    value={selectedChipType[assinatura.id] || ''}
                                                    onChange={(e) => setSelectedChipType(prev => ({ ...prev, [assinatura.id]: e.target.value as 'ESIM' | 'FISICO' | '' }))}
                                                    style={{
                                                        backgroundColor: '#2a2a2a',
                                                        color: '#fff',
                                                        border: '1px solid #444',
                                                        padding: '10px 15px',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        width: '100%',
                                                        maxWidth: '300px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <option value="">Selecione o tipo de chip</option>
                                                    <option value="ESIM">e-SIM</option>
                                                    <option value="FISICO">Chip Físico</option>
                                                </select>
                                            </div>

                                            {selectedChipType[assinatura.id] === 'FISICO' && (
                                                <div style={{ marginBottom: '20px' }}>
                                                    <label style={{ color: '#fff', display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                                                        ICCID do Chip:*
                                                    </label>
                                                    <input
                                                        type="text"
                                                        placeholder="Digite o ICCID do chip"
                                                        value={iccidInput[assinatura.id] || ''}
                                                        onChange={(e) => setIccidInput(prev => ({ ...prev, [assinatura.id]: e.target.value }))}
                                                        style={{
                                                            width: '100%',
                                                            backgroundColor: '#2a2a2a',
                                                            color: '#fff',
                                                            border: '1px solid #444',
                                                            padding: '10px 15px',
                                                            borderRadius: '6px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                    <p style={{ color: '#999', fontSize: '12px', margin: '5px 0 0 0' }}>
                                                        O ICCID é o número de identificação do chip físico
                                                    </p>
                                                </div>
                                            )}

                                            <button
                                                onClick={() => {
                                                    const chipType = selectedChipType[assinatura.id];
                                                    if (!chipType) {
                                                        alert('Por favor, selecione o tipo de chip');
                                                        return;
                                                    }
                                                    const iccid = chipType === 'FISICO' ? iccidInput[assinatura.id] : undefined;
                                                    ativarMVNO(assinatura.id, chipType, iccid);
                                                }}
                                                disabled={activatingMvno === assinatura.id || !selectedChipType[assinatura.id]}
                                                style={{
                                                    backgroundColor: '#6EE7B7',
                                                    color: '#000',
                                                    border: 'none',
                                                    padding: '12px 24px',
                                                    borderRadius: '6px',
                                                    fontWeight: 'bold',
                                                    cursor: (activatingMvno === assinatura.id || !selectedChipType[assinatura.id]) ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px',
                                                    opacity: (activatingMvno === assinatura.id || !selectedChipType[assinatura.id]) ? 0.6 : 1,
                                                    marginTop: '10px'
                                                }}
                                            >
                                                {activatingMvno === assinatura.id ? 'Ativando...' : 'Ativar Linha'}
                                            </button>

                                            
                                        </div>
                                    // ) : assinatura.mvno?.status === 'AGUARDANDO_ATIVACAO' ? (
                                    //     <div style={{ padding: '15px', backgroundColor: '#2a2a2a', borderRadius: '8px', borderLeft: '4px solid #FF9800' }}>
                                    //         <p style={{ color: '#FF9800', margin: 0, fontSize: '14px' }}>
                                    //             ⏳ Aguardando ativação da linha. Sua solicitação está sendo processada.
                                    //         </p>
                                    //     </div>
                                    ) : assinatura.mvno?.status === 'SUSPENSO' ? (
                                        <div style={{ padding: '15px', backgroundColor: '#2a2a2a', borderRadius: '8px', borderLeft: '4px solid #F44336' }}>
                                            <p style={{ color: '#F44336', margin: 0, fontSize: '14px' }}>
                                                🚫 Sua linha está suspensa. Entre em contato com o suporte para mais informações.
                                            </p>
                                        </div>
                                    ) : assinatura.mvno?.status === 'CANCELADO' ? (
                                        <div style={{ padding: '15px', backgroundColor: '#2a2a2a', borderRadius: '8px', borderLeft: '4px solid #9E9E9E' }}>
                                            <p style={{ color: '#9E9E9E', margin: 0, fontSize: '14px' }}>
                                                ❌ Sua linha foi cancelada.
                                            </p>
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            {assinatura.a_receber && assinatura.a_receber.length > 0 && (
                                <div>
                                    <h4 style={{ color: '#fff', margin: '15px 0 10px 0', fontSize: '16px' }}>Pagamentos</h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {assinatura.a_receber.map((recebimento) => (
                                            <div
                                                key={recebimento.id}
                                                style={{
                                                    backgroundColor: '#2a2a2a',
                                                    padding: '15px',
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: '10px'
                                                }}
                                            >
                                                <div>
                                                    <p style={{ color: '#6EE7B7', fontSize: '18px', fontWeight: 'bold', margin: '0 0 5px 0' }}>
                                                        R$ {parseFloat(recebimento.valor).toFixed(2)}
                                                    </p>
                                                    <p style={{ color: '#fff', fontSize: '14px', margin: '0 0 5px 0' }}>
                                                        {recebimento.descricao}
                                                    </p>
                                                    <p style={{ color: '#999', fontSize: '14px', margin: 0 }}>
                                                        Vencimento: {recebimento.vencimento}
                                                    </p>
                                                </div>
                                                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end' }}>
                                                    <div>
                                                        <p style={{ margin: '0 0 5px 0' }}>
                                                            {renderPaymentStatus(recebimento.status)}
                                                        </p>
                                                        <p style={{ color: '#999', fontSize: '14px', margin: 0 }}>
                                                            {recebimento.tipo_recebimento}
                                                        </p>
                                                    </div>
                                                    {recebimento.status === 'A' && (
                                                        <button
                                                            onClick={() => navigate('/pagamento', {
                                                                state: {
                                                                    id: recebimento.id,
                                                                    contrato_id: assinatura.id,
                                                                    valor: recebimento.valor
                                                                }
                                                            })}
                                                            style={{
                                                                backgroundColor: '#6EE7B7',
                                                                color: '#000',
                                                                border: 'none',
                                                                padding: '8px 20px',
                                                                borderRadius: '6px',
                                                                fontWeight: 'bold',
                                                                cursor: 'pointer',
                                                                fontSize: '14px'
                                                            }}
                                                        >
                                                            Pagar
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}

                <a href="/minhas-faturas" className="card-link">
                    <div>
                        <b>Últimas faturas</b>
                        <span>Ver histórico de pagamento</span>
                    </div>

                    <svg width="60" height="60" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M46.4508 79.3999L65.8508 59.9999L46.4508 40.5999C44.5008 38.6499 44.5008 35.4999 46.4508 33.5499C48.4008 31.5999 51.5508 31.5999 53.5008 33.5499L76.4508 56.4999C78.4008 58.4499 78.4008 61.5999 76.4508 63.5499L53.5008 86.4999C51.5508 88.4499 48.4008 88.4499 46.4508 86.4999C44.5508 84.5499 44.5008 81.3499 46.4508 79.3999Z" fill="white" />
                    </svg>
                </a>
            </div>

            {showCancelModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#1a1a1a',
                        padding: '30px',
                        borderRadius: '12px',
                        maxWidth: '500px',
                        width: '90%',
                        border: '1px solid #333'
                    }}>
                        <h3 style={{ color: '#fff', marginTop: 0 }}>Solicitar Cancelamento</h3>
                        <p style={{ color: '#ccc', fontSize: '14px' }}>
                            O cancelamento será processado no próximo vencimento. Você pode escolher quais serviços deseja cancelar:
                        </p>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', color: '#fff', marginBottom: '10px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={cancelOptions[showCancelModal]?.internet ?? true}
                                    onChange={(e) => setCancelOptions(prev => ({
                                        ...prev,
                                        [showCancelModal]: { ...prev[showCancelModal], internet: e.target.checked }
                                    }))}
                                    style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                Cancelar Internet
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', color: '#fff', marginBottom: '10px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={cancelOptions[showCancelModal]?.tv ?? true}
                                    onChange={(e) => setCancelOptions(prev => ({
                                        ...prev,
                                        [showCancelModal]: { ...prev[showCancelModal], tv: e.target.checked }
                                    }))}
                                    style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                Cancelar TV
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', color: '#fff', marginBottom: '10px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={cancelOptions[showCancelModal]?.mvno ?? true}
                                    onChange={(e) => setCancelOptions(prev => ({
                                        ...prev,
                                        [showCancelModal]: { ...prev[showCancelModal], mvno: e.target.checked }
                                    }))}
                                    style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                Cancelar Telefonia Móvel (MVNO)
                            </label>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ color: '#fff', display: 'block', marginBottom: '8px', fontSize: '14px' }}>
                                Motivo do cancelamento (opcional):
                            </label>
                            <textarea
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Digite o motivo do cancelamento..."
                                style={{
                                    width: '100%',
                                    backgroundColor: '#2a2a2a',
                                    color: '#fff',
                                    border: '1px solid #444',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    resize: 'vertical',
                                    minHeight: '80px',
                                    fontFamily: 'inherit'
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowCancelModal(null);
                                    setCancelReason('');
                                }}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#4A5568',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleCancelContract(showCancelModal)}
                                disabled={cancellingContract === showCancelModal}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#F44336',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontWeight: 'bold',
                                    cursor: cancellingContract === showCancelModal ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    opacity: cancellingContract === showCancelModal ? 0.6 : 1
                                }}
                            >
                                {cancellingContract === showCancelModal ? 'Processando...' : 'Confirmar Cancelamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="security">
                <svg height="15" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.3934 0H1.85395C1.36225 0 0.890691 0.195326 0.543008 0.543008C0.195326 0.890691 0 1.36225 0 1.85395V8.66372C0 19.047 8.78539 22.4918 10.5443 23.077C10.92 23.2048 11.3274 23.2048 11.703 23.077C13.4643 22.4918 22.2474 19.047 22.2474 8.66372V1.85395C22.2474 1.36225 22.052 0.890691 21.7044 0.543008C21.3567 0.195326 20.8851 0 20.3934 0ZM16.4144 8.07162L9.92557 14.5604C9.83948 14.6466 9.73724 14.715 9.62471 14.7616C9.51218 14.8083 9.39155 14.8323 9.26974 14.8323C9.14792 14.8323 9.02729 14.8083 8.91476 14.7616C8.80223 14.715 8.69999 14.6466 8.6139 14.5604L5.83298 11.7795C5.65904 11.6056 5.56133 11.3697 5.56133 11.1237C5.56133 10.8777 5.65904 10.6418 5.83298 10.4678C6.00692 10.2939 6.24283 10.1962 6.48881 10.1962C6.7348 10.1962 6.97071 10.2939 7.14465 10.4678L9.26974 12.5929L15.1027 6.75995C15.1888 6.67383 15.2911 6.60551 15.4036 6.5589C15.5161 6.51229 15.6368 6.4883 15.7585 6.4883C15.8803 6.4883 16.001 6.51229 16.1135 6.5589C16.226 6.60551 16.3283 6.67383 16.4144 6.75995C16.5005 6.84608 16.5688 6.94832 16.6154 7.06085C16.662 7.17338 16.686 7.29399 16.686 7.41579C16.686 7.53759 16.662 7.65819 16.6154 7.77072C16.5688 7.88325 16.5005 7.9855 16.4144 8.07162Z" fill="#6EE7B7" /></svg>
                <span>Todos os seus dados estão seguros e encriptados.</span>
            </div>

            <img src={fxRightImg} className="fx-right-img" alt="decoration" />
            <img src={fxLeftImg} className="fx-left-img" alt="decoration" />
        </React.Fragment>
    )
}

export default Subscription;
